import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fetchProductLedger, type ProductLedger } from "@/lib/catalog/productLedgerStore";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { Loader2, FileSpreadsheet } from "lucide-react";
import { useEffect, useState } from "react";

function money(amount: number): string {
  return formatCurrency(Math.abs(amount));
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function ProductLedgerDialog({
  open,
  productId,
  productName,
  onOpenChange,
}: {
  open: boolean;
  productId: string | number | null;
  productName?: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<ProductLedger | null>(null);

  useEffect(() => {
    if (!open || productId == null || String(productId).trim() === "") {
      setLedger(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchProductLedger(productId)
      .then((data) => {
        if (!cancelled) setLedger(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && err.response?.status === 403) {
          setError("You don’t have permission to view this product ledger.");
          return;
        }
        if (isAxiosError(err) && err.response?.status === 404) {
          setError("Product not found.");
          return;
        }
        setError("Could not load the product ledger.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  const rows = ledger?.lines ?? [];
  const titleName = ledger?.product.name || productName || (productId != null ? `#${productId}` : "Product");
  const showPartnerCards =
    (ledger?.totals.partnerReceivable ?? 0) > 0.005 || (ledger?.totals.partnerCapital ?? 0) > 0.005;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-4 overflow-x-hidden overflow-y-hidden p-5 sm:max-w-5xl bg-background/95 backdrop-blur-md border border-border/80 rounded-xl shadow-xl">
        <DialogHeader className="shrink-0">
          <div className="flex items-center gap-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="text-base font-bold text-foreground leading-none">
                Product Ledger
              </DialogTitle>
              <span className="text-xs text-muted-foreground mt-1.5 leading-normal">
                Detailed accounting transactions and cost breakdown for {titleName}
              </span>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-56 shrink-0 items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-xs font-semibold text-muted-foreground">Loading product ledger...</span>
            </div>
          </div>
        ) : error ? (
          <p className="shrink-0 py-8 text-center text-sm font-semibold text-destructive">{error}</p>
        ) : !ledger || rows.length === 0 ? (
          <p className="shrink-0 py-8 text-center text-sm font-medium text-muted-foreground">
            No ledger entries for this product yet.
          </p>
        ) : (
          <>
            <div
              className={cn(
                "grid gap-3 shrink-0",
                showPartnerCards ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3",
              )}
            >
              <div className="rounded-xl border border-emerald-500/20 dark:border-emerald-500/30 bg-emerald-500/5 dark:bg-emerald-500/10 p-3.5 flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-400 tracking-wider">
                  Total Landed Cost
                </span>
                <span className="text-xl font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums leading-none text-right">
                  {money(ledger.totals.vehicleCost)}
                </span>
              </div>
              <div className="rounded-xl border border-rose-500/20 dark:border-rose-500/30 bg-rose-500/5 dark:bg-rose-500/10 p-3.5 flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold text-rose-600 dark:text-rose-400 tracking-wider">
                  Supplier Payable Balance
                </span>
                <span className="text-xl font-extrabold text-rose-600 dark:text-rose-400 tabular-nums leading-none text-right">
                  {money(ledger.totals.supplierPayable)}
                </span>
              </div>
              {showPartnerCards ? (
                <>
                  <div className="rounded-xl border border-sky-500/20 dark:border-sky-500/30 bg-sky-500/5 dark:bg-sky-500/10 p-3.5 flex flex-col gap-1">
                    <span className="text-[10px] uppercase font-bold text-sky-600 dark:text-sky-400 tracking-wider">
                      Partner Receivable
                    </span>
                    <span className="text-xl font-extrabold text-sky-700 dark:text-sky-300 tabular-nums leading-none text-right">
                      {money(ledger.totals.partnerReceivable)}
                    </span>
                  </div>
                  <div className="rounded-xl border border-violet-500/20 dark:border-violet-500/30 bg-violet-500/5 dark:bg-violet-500/10 p-3.5 flex flex-col gap-1">
                    <span className="text-[10px] uppercase font-bold text-violet-600 dark:text-violet-400 tracking-wider">
                      Partner Capital
                    </span>
                    <span className="text-xl font-extrabold text-violet-700 dark:text-violet-300 tabular-nums leading-none text-right">
                      {money(ledger.totals.partnerCapital)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-blue-500/20 dark:border-blue-500/30 bg-blue-500/5 dark:bg-blue-500/10 p-3.5 flex flex-col gap-1">
                  <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 tracking-wider">
                    Total Vouched Amount
                  </span>
                  <span className="text-xl font-extrabold text-blue-700 dark:text-blue-300 tabular-nums leading-none text-right">
                    {money(ledger.totals.journalDebit)}
                  </span>
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-contain rounded-xl border border-border/50 bg-card/20 shadow-xs">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="h-10 px-4 text-left font-semibold w-[110px]">Date</th>
                    <th className="h-10 px-4 text-left font-semibold w-[22%]">Particular</th>
                    <th className="h-10 px-4 text-left font-semibold">Description</th>
                    <th className="h-10 px-4 text-right font-semibold w-[130px]">Debit</th>
                    <th className="h-10 px-4 text-right font-semibold w-[130px]">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {rows.map((line) => (
                    <tr key={line.id} className="transition-colors hover:bg-muted/10">
                      <td className="px-1 py-1 whitespace-nowrap text-xs text-muted-foreground font-medium">
                        {formatDate(line.date)}
                      </td>
                      <td className="px-1 py-1">
                        <span className="font-bold text-foreground text-sm leading-snug">
                          {line.particular || "—"}
                        </span>
                      </td>
                      <td className="px-1 py-1">
                        <span className="text-[11px] text-muted-foreground/80 leading-normal">
                          {line.description || "—"}
                        </span>
                      </td>
                      <td className="px-1 py-1 text-right">
                        {line.debit > 0.005 ? (
                          <span className="text-xs font-normal tabular-nums text-emerald-600 dark:text-emerald-400">
                            {money(line.debit)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/30 font-normal text-xs">—</span>
                        )}
                      </td>
                      <td className="px-1 py-1 text-right">
                        {line.credit > 0.005 ? (
                          <span className="text-xs font-normal tabular-nums text-rose-600 dark:text-rose-400">
                            {money(line.credit)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/30 font-normal text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/20 border-t border-border font-bold">
                    <td colSpan={3} className="px-1 py-1 text-sm font-semibold text-foreground">
                      Total
                    </td>
                    <td className="px-1 py-1 text-right tabular-nums text-sm font-bold text-foreground">
                      {money(ledger.totals.journalDebit)}
                    </td>
                    <td className="px-1 py-1 text-right tabular-nums text-sm font-bold text-foreground">
                      {money(ledger.totals.journalCredit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
